import { getQueryTextByHash } from "@/helpers/apis";
import {
  CodeBlock,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Typography,
  Spinner,
  Stack,
} from "@altimateai/lego";
import { QUERY_TEXT_API_KEY } from "./constants";
import { QueryTextResponse } from "./types";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useDebounce } from "@/helpers/hooks";

interface Props {
  queryText: string;
  queryHash?: string;
  fullText?: string;
}

const QueryTextWithHover = ({ queryText, queryHash, fullText }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const debouncedIsOpen = useDebounce(isOpen, 300);
  const hasFullText = Boolean(fullText);

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const { data, isLoading, refetch } = useQuery<QueryTextResponse>({
    queryKey: [QUERY_TEXT_API_KEY, queryHash],
    queryFn: async () => {
      const response = await getQueryTextByHash(queryHash!);
      if (!response) {
        throw new Error("Failed to fetch query text");
      }
      return response;
    },
    enabled: false,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const handleMouseEnter = () => {
    setIsOpen(true);
    if (!hasFullText && queryHash && !data) {
      refetch();
    }
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const displayText = fullText || data?.query_text || queryText;

  return (
    <Popover open={debouncedIsOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="al-text-left"
      >
        <span
          className="al-text-brand al-cursor-pointer"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleMouseEnter();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="View SQL query text"
        >
          {queryText}
        </span>
      </PopoverTrigger>
      <PopoverContent
        className={"al-w-[min(768px,calc(100vw-32px))] al-overflow-hidden"}
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={{ left: 16, right: 16, top: 16, bottom: 16 }}
        avoidCollisions={true}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={stopPropagation}
      >
        <Stack direction={"column"}>
          <Typography variant={"h3"} weight={"semibold"}>
            SQL Text
          </Typography>
          {!hasFullText && isLoading ? (
            <Stack>
              <Spinner size={"md"} />
              Fetching query...
            </Stack>
          ) : (
            <div className="al-overflow-auto al-max-h-[350px]">
              <CodeBlock
                code={displayText}
                language="sql"
                fileName=""
                showLineNumbers={false}
                theme="dark"
                editorTheme="tomorrow"
                titleActions={null}
              />
            </div>
          )}
        </Stack>
      </PopoverContent>
    </Popover>
  );
};

export default QueryTextWithHover;
